import { produce } from 'immer';
import { debounce } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { atomFamily, useRecoilState } from 'recoil';
import { setDeep } from '../../../utils/object';
import { ChartEntity } from '../../chart/types';
import { ChartOptionsV2 } from '../../chart/types-v2';
import { DefaultBarChartUiOptions } from '../../chart/utils/echart';
import { SelectChartByIdState, updateChart } from '../ChartsState';

export type YCol = {
  colName: string;
  customLabel?: string;
  valueType: string;
  chartType: string;
  color?: string;
};

export type LineChartUiOptions = {
  version?: number;
  chartOptions?: {
    showLegend?: boolean;
    enableStacking?: boolean;
    normalizeData?: boolean;
  };
  xAxisOptions?: {
    label?: string;
    tickFormat?: string;
    logarithmic?: boolean;
    sortValues?: boolean;
    reverseValue?: boolean;
  };
  yAxisOptions?: {
    label?: string;
    tickFormat?: string;
    labelFormat?: string;
    logarithmic?: boolean;
    enableRightYAxis?: boolean;
  };
  dataOptions?: {
    groupByCol?: string;
    xCol?: {
      colName: string;
      customLabel?: string;
      valueType: string;
    };
    yCols?: YCol[];
    groupByYCols?: YCol[];
  };
};

export type SetLineChartOptions = (n: ChartOptionsV2) => void;

export const LineChartOptionsState = atomFamily<ChartOptionsV2, string>({
  key: 'LineChartOptionsState',
  default: {
    displayName: 'Line chart',
    uiOptions: DefaultBarChartUiOptions,
  },
});

export function useLineChartOptions(chartId: string) {
  const [chart, setChart] = useRecoilState(
    SelectChartByIdState({ id: chartId })
  );
  const [lineChartOptions, setLineChartOptionsState] = useRecoilState(
    LineChartOptionsState(chartId)
  );

  const [updating, setUpdating] = useState(false);

  const debouncedEventHandler = useMemo(
    () =>
      debounce((nLineChartOptions: ChartOptionsV2) => {
        // trigger rerender chart

        const nChart: ChartEntity = {
          ...chart,
          ...nLineChartOptions,
        } as ChartEntity;

        setChart(nChart);

        // TODO: call api to update chart
        // eslint-disable-next-line
        // console.log('api update & trigger rerender chart', nChart);
        updateChart({ chart: nChart });

        setUpdating(false);
      }, 600),
    [chart, setChart]
  );

  const setChartOptions = useCallback(
    (nLineChartOptions: ChartOptionsV2) => {
      setUpdating(true);

      // update options
      setLineChartOptionsState(nLineChartOptions);
      // trigger rerender by updating chart
      debouncedEventHandler(nLineChartOptions);
    },
    [debouncedEventHandler]
  );

  const setChartOptionsByMap = useCallback(
    // eslint-disable-next-line
    (valueMap: Record<string, any>) => {
      setChartOptions(
        produce(lineChartOptions, (draft) => {
          setDeep(draft, valueMap);
        })
      );
    },
    [lineChartOptions, setChartOptions]
  );

  return [
    lineChartOptions,
    updating,
    {
      setChartOptions,
      setChartOptionsByMap,
    },
  ] as const;
}
